import React from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, 
  FileSpreadsheet,
  AlertTriangle,
  Zap,
  Flame,
  Home,
  Ruler,
  Droplets
} from 'lucide-react';

const projects = [
  {
    id: 1,
    title: "DPE Maison individuelle",
    description: "Diagnostic de performance énergétique réalisé pour une maison de 120m². Notre intervention a permis d'établir un bilan énergétique complet avec des recommandations personnalisées pour l'amélioration de la performance.",
    imageUrl: "https://i.postimg.cc/2jQFM6d9/diag.png",
    category: "DPE",
    icons: [<FileSpreadsheet key="dpe" className="h-5 w-5 text-dk-blue" />]
  },
  {
    id: 2,
    title: "Diagnostic Amiante",
    description: "Inspection et recherche d'amiante effectuées dans un appartement ancien avant travaux. Notre expertise a permis d'identifier avec précision les matériaux contenant de l'amiante selon les normes en vigueur.",
    imageUrl: "https://i.postimg.cc/cJPByt52/amiante.png",
    category: "Amiante",
    icons: [<AlertTriangle key="amiante" className="h-5 w-5 text-dk-blue" />]
  },
  {
    id: 3,
    title: "Installation électrique",
    description: "Diagnostic électrique complet réalisé dans un local commercial. Notre intervention a permis d'établir un état détaillé des installations avec identification précise des points à mettre aux normes.",
    imageUrl: "https://www.bestdiagimmo.com/userfiles/image/electricite2.jpg",
    category: "Électricité",
    icons: [<Zap key="elec" className="h-5 w-5 text-dk-blue" />]
  },
  {
    id: 4,
    title: "Diagnostic Gaz",
    description: "Contrôle approfondi effectué sur l'installation gaz d'une maison en vente. Notre expertise a permis de vérifier la conformité complète du réseau et des équipements.",
    imageUrl: "https://toutvabiens.com/wp-content/uploads/2023/02/gaz.webp",
    category: "Gaz",
    icons: [<Flame key="gaz" className="h-5 w-5 text-dk-blue" />]
  },
  {
    id: 5,
    title: "Diagnostic Termites",
    description: "Inspection détaillée pour la recherche de termites. Notre intervention a permis d'établir une cartographie précise des zones inspectées avec recommandations appropriées.",
    imageUrl: "https://www.abe-diagnostic.com/application/files/2116/5297/0647/A.B.E-diagnostic-termite.jpg",
    category: "Termites",
    icons: [<Home key="termites" className="h-5 w-5 text-dk-blue" />]
  },
  {
    id: 6,
    title: "Mesurages Carrez / Boutin",
    description: "Mesurage professionnel effectué selon les lois Carrez et Boutin. Notre intervention a permis d'établir avec précision les surfaces habitables et annexes avec plan détaillé.",
    imageUrl: "https://i.postimg.cc/VkYt3Wsd/maison-verte.png",
    category: "Mesurage",
    icons: [<Ruler key="carrez" className="h-5 w-5 text-dk-blue" />]
  },
  {
    id: 7,
    title: "CREP - Diagnostic Plomb",
    description: "Constat de Risque d'Exposition au Plomb (CREP) réalisé dans une maison ancienne. Notre expertise a permis d'identifier et de localiser la présence de plomb dans les revêtements pour assurer la sécurité des occupants.",
    imageUrl: "https://i.postimg.cc/7YX1PWWg/plomb.png",
    category: "Plomb",
    icons: [<Droplets key="plomb" className="h-5 w-5 text-dk-blue" />]
  }
];

const Realisations = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-32 pb-16 px-4 bg-gradient-to-br from-white to-gray-100"
    >
      <div className="container mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center text-gray-900">
          Nos Réalisations
        </h1>
        <p className="text-xl text-center max-w-3xl mx-auto mb-12 text-gray-600">
          Découvrez quelques-uns de nos diagnostics immobiliers récents.
          Chaque intervention est réalisée avec professionnalisme, précision et dans le respect des normes en vigueur.
        </p>
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {/* First row - 4 cards */}
          {projects.slice(0, 4).map((project) => (
            <motion.div
              key={project.id}
              variants={itemVariants}
              className="bg-gradient-to-br from-white to-gray-50 rounded-lg overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl group h-[500px] flex flex-col"
            >
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={project.imageUrl} 
                  alt={project.title} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute top-0 right-0 bg-dk-blue text-white px-4 py-2 m-3 rounded-full text-sm font-medium">
                  {project.category}
                </div>
              </div>
              
              <div className="p-6 bg-gradient-to-br from-white to-gray-50 flex-grow flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-2 mr-3">
                    {project.icons.map((icon, i) => (
                      <span key={i} className="transform group-hover:scale-110 transition-transform duration-300">
                        {icon}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 group-hover:text-dk-blue transition-colors">
                    {project.title}
                  </h3>
                </div>
                
                <p className="text-gray-600 mb-4 leading-relaxed flex-grow">
                  {project.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Second row - 3 cards centered */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"
        >
          {projects.slice(4).map((project) => (
            <motion.div
              key={project.id}
              variants={itemVariants}
              className="bg-gradient-to-br from-white to-gray-50 rounded-lg overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl group h-[500px] flex flex-col"
            >
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={project.imageUrl} 
                  alt={project.title} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute top-0 right-0 bg-dk-blue text-white px-4 py-2 m-3 rounded-full text-sm font-medium">
                  {project.category}
                </div>
              </div>
              
              <div className="p-6 bg-gradient-to-br from-white to-gray-50 flex-grow flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-2 mr-3">
                    {project.icons.map((icon, i) => (
                      <span key={i} className="transform group-hover:scale-110 transition-transform duration-300">
                        {icon}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 group-hover:text-dk-blue transition-colors">
                    {project.title}
                  </h3>
                </div>
                
                <p className="text-gray-600 mb-4 leading-relaxed flex-grow">
                  {project.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
        
        <div className="text-center mt-16">
          <p className="text-xl mb-8 text-gray-700">
            Besoin d'un diagnostic immobilier professionnel ? 
            <br />
            Notre équipe certifiée est à votre disposition pour répondre à toutes vos exigences.
          </p>
          <a 
            href="tel:0789307500"
            className="btn-primary inline-flex items-center text-lg px-8 py-4 transform hover:scale-105 transition-all duration-300"
          >
            Nous contacter
            <Phone className="h-5 w-5 ml-2" />
          </a>
        </div>
      </div>
    </motion.div>
  );
};

export default Realisations;